import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Collapse,
  Flex,
  HStack,
  Spinner,
  Stack,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import DailyAlertsCard from '../../../components/daily-alerts-card/DailyAlertsCard';
import OfficeNotesCard from '../../../components/office-notes-card/OfficeNotesCard';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import { ProviderAbsence } from '../../../types/absences/types';
import { CustomField } from '../../../types/custom-fields/types';
import {
  CustomFieldValue,
  Provider,
} from '../../../types/practices/types';
import {
  getCustomFieldValue,
  getProviderCustomFieldLabel,
} from '../../../utils/customFieldUtils';
import { useSupervisionRoleLabel } from '../../../hooks/useSupervisionRoleLabel';
import { getNewPatientPriorityProps } from '../../../utils/getNewPatientPriorityProps';
import { getProviderTypeFromData } from '../../../utils/getProviderTypeFromData';
import { constructPracticeAddress } from '../../../utils/practiceUtils';
import { isExternalProvider } from '../../../utils/externalEntity';
import { isActiveProviderBridge } from '../../../utils/providerBridgeUtils';
import ExternalAffordance from '../../../components/external-indicator/ExternalAffordance';
import {
  BOTH_FIELDS,
  PCP_ONLY_FIELDS,
  SPECIALIST_ONLY_FIELDS,
} from '../../../utils/providerCustomFields';
import {
  applySchedulingGroupOrder,
  buildServices,
} from '../../../utils/providerFieldMapping';
import { sanitizeHtml } from '../../../utils/textConversion';
import AthenaDepartmentLink from '../practices/AthenaDepartmentLink';
import CrossCoverageAllApptsBadge from './CrossCoverageAllApptsBadge';
import ClosestUrgentCareBadge from './ClosestUrgentCareBadge';
import CrossCoverageSickBadge from './CrossCoverageSickBadge';
import { COVERAGE_LABELS } from './coverageTypes';
import ExtractMedicareWellness from './extractMedicareWellness';
import ProviderAbsencesCard from './ProviderAbsencesCard';
import ProviderPacStatus from './ProviderPacStatus';
import MultiClinicBadge from '../../../components/multi-clinic-badge/MultiClinicBadge';

type Service = {
  name: string;
  detail: string;
  category: string;
  isRichText?: boolean;
};
interface PracticeSummary {
  id: string;
  name: string;
}
interface ProviderCompactCardProps {
  provider: Provider;
  officeHoursLoading?: boolean;
  practices?: PracticeSummary[];
  absences?: ProviderAbsence[];
  loading?: boolean;
  error?: string | null;
  refreshAbsences?: () => Promise<void>;
  customFields?: CustomField[]; // Add customFields prop
  isRefreshing?: boolean; // Indicates if parent is refreshing data
  isExpanded?: boolean; // Controls if card is expanded by default
  isPracticesLoading?: boolean; // Indicates if practices are being fetched
  duplicateProviders?: Record<string, string>; // Duplicate providers data
  isCustomFieldsLoading?: boolean; // Add loading state for custom fields
  customFieldsError?: string | null; // Add error state for custom fields
  specialtyOptions?: { label: string; value: string }[]; // Specialty options from parent
  isSpecialtyOptionsLoading?: boolean; // Loading state for specialty options
  isExternalContext?: boolean; // True when all providers in this context are external (e.g. under an external practice)
}

const ProviderCompactCard: React.FC<ProviderCompactCardProps> = ({
  provider,
  practices,
  absences,
  loading,
  error,
  refreshAbsences,
  customFields: passedCustomFields,
  isRefreshing = false,
  isPracticesLoading = false,
  isExpanded = false,
  duplicateProviders,
  isCustomFieldsLoading = false,
  customFieldsError = null,
  specialtyOptions = [],
  isSpecialtyOptionsLoading = false,
  isExternalContext = false,
}) => {
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: isExpanded,
  });
  const providerExternalTablespace = (
    provider as Provider & { externalTablespace?: string | null }
  ).externalTablespace;
  const providerIsExternal = isExternalProvider(provider) || isExternalContext;
  const [services, setServices] = useState<Service[] | []>([]);
  const supervisionRoleLabel = useSupervisionRoleLabel(provider);

  // Update isOpen when isExpanded prop changes
  useEffect(() => {
    if (isExpanded !== isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]); // Only depend on isExpanded to avoid infinite loop

  const newPatientProps = getNewPatientPriorityProps(
    getCustomFieldValue(provider.customFields, 'New-Patient')
  );
  const getServiceLabel = (name: string) =>
    getProviderCustomFieldLabel(name, {
      'Patients-Virtual-Waiting-Schedule':
        'Patients to place on Virtual Waiting schedule',
      'Supervision-Physician': supervisionRoleLabel,
      'Additional-Location-Days': 'Additional Location Days',
      'New-Telehealth': 'New Telehealth',
      'Out-Of-State-Telehealth': 'Out of State Telehealth',
      Telehealth: 'Telehealth',
      'Takes-Walk-Ins': 'Takes Walk-ins',
    });
  const navigate = useNavigate();

  // Helper function to get Athena data from provider bridge
  const getAthenaDataFromBridge = () => {
    if (
      !provider.providerBridge ||
      provider.providerBridge.length === 0
    ) {
      return {
        departmentName: undefined,
        departmentLink: undefined,
        practiceName: undefined,
        practiceLocation: undefined,
      };
    }

    const activeBridge =
      provider.providerBridge.find((bridge) => isActiveProviderBridge(bridge)) ||
      provider.providerBridge[0];

    // Find matching practice from relatedPractices
    const practice = provider.relatedPractices?.find(
      (p) => p.id === activeBridge?.practiceId
    );

    // Build location string (city, state)
    const location = practice
      ? [practice.city, practice.state].filter(Boolean).join(', ')
      : undefined;

    const departmentName =
      activeBridge?.metadata?.['Athena-Department'];
    const departmentLink = activeBridge?.metadata?.['Athena-Link'];

    return {
      departmentName:
        departmentName &&
        departmentName.trim() !== '' &&
        departmentName !== '-'
          ? departmentName.trim()
          : undefined,
      departmentLink:
        departmentLink &&
        departmentLink.trim() !== '' &&
        departmentLink !== '-' &&
        departmentLink !== 'null' &&
        departmentLink !== 'undefined'
          ? departmentLink.trim()
          : undefined,
      practiceName: practice?.name,
      practiceLocation: location,
    };
  };

  const athenaData = getAthenaDataFromBridge();

  // Detect provider type for field filtering
  const providerType = getProviderTypeFromData(
    provider?.customFields,
    provider?.specialty
  );
  const isPCP = providerType === 'Pcp';

  // Build allowed fields based on provider type
  const allowedFields = isPCP
    ? [...BOTH_FIELDS, ...PCP_ONLY_FIELDS]
    : [...BOTH_FIELDS, ...SPECIALIST_ONLY_FIELDS];

  // Filter services to only show allowed fields
  const filteredServices = services.filter((service) => {
    // Hide legacy "Telehealth" field; New-Telehealth and Out-Of-State shown for all provider types
    if (service.name === 'Telehealth') {
      return false;
    }
    // Additional filtering for PCP-only fields
    if (
      !isPCP &&
      (service.name === 'Next-In-Line' ||
        service.name === 'Takes-Walk-Ins' ||
        service.name === 'Cross-Coverage-for-Sick-Market' ||
        service.name === 'Closest-Urgent-Care')
    ) {
      return false;
    }
    return allowedFields.includes(service.name);
  });

  useEffect(() => {
    const allServices = buildServices(provider.customFields ?? {});
    setServices(allServices);
  }, [provider.customFields]);

  // Helper function to get the actual value for a field
  // For special fields, reads directly from customFields; otherwise uses service.detail
  const getFieldValue = useCallback(
    (service: Service): string => {
      const specialFields = [
        'New-Telehealth',
        'Out-Of-State-Telehealth',
        'Telehealth',
        'Medicare-Wellness',
        'Cross-Coverage-Sick',
        'Cross-Coverage-for-Sick-Market',
        'Closest-Urgent-Care',
        'Cross-Coverage',
        'Supervision-Physician',
      ];

      // For special fields, always read from customFields
      if (specialFields.includes(service.name)) {
        const value = getCustomFieldValue(
          provider?.customFields,
          service.name
        );
        return value ? String(value) : '';
      }

      // For other fields, use service.detail
      return service.detail || '';
    },
    [provider?.customFields]
  );

  // Helper function to render Yes/No value with icon
  const renderYesNoValue = useCallback((value: string) => {
    const normalized = value.toLowerCase().trim();
    const isYes = normalized === 'yes';
    const isNo = normalized === 'no';

    if (!isYes && !isNo) {
      return null;
    }

    return (
      <Flex align="center" justify="center" gap="0.5rem">
        <Flex
          bg={isYes ? 'green.500' : 'red.500'}
          boxSize="1.25rem"
          borderRadius="50%"
          align="center"
          justify="center"
        >
          {isYes ? (
            <CheckIcon
              color="white"
              width="0.75rem"
              height="0.75rem"
            />
          ) : (
            <CloseIcon
              color="white"
              width="0.75rem"
              height="0.75rem"
            />
          )}
        </Flex>
        <Text
          fontSize="0.875rem"
          fontWeight="700"
          color={isYes ? 'green.500' : 'red.500'}
        >
          {isYes ? 'Yes' : 'No'}
        </Text>
      </Flex>
    );
  }, []);

  // Get scheduling and treatment fields from filtered services.
  // Coverage fields are grouped/ordered the same way as on the provider detail page.
  const schedulingFields = applySchedulingGroupOrder(
    filteredServices.filter(
      (service) => service.category === 'Scheduling'
    ),
    isPCP
  ).filter((service) => {
    if (service.name !== 'Cross-Coverage-for-Sick-Market' && service.name !== 'Cross-Coverage-Sick') return true;
    const cf = provider.customFields[service.name] as CustomFieldValue[] | undefined;
    if (!Array.isArray(cf) || cf.length === 0) return false;
    return cf.some((r) => {
      const val = typeof r.value === 'string' ? r.value.trim() : '';
      const providers = r.metadata?.providers as unknown[] | undefined;
      const coverageType = r.metadata?.coverageType as string | undefined;
      return (Boolean(val) && val !== 'None') || Boolean(coverageType && COVERAGE_LABELS[coverageType]) || (Array.isArray(providers) && providers.length > 0);
    });
  });
  const treatmentFields = filteredServices.filter(
    (service) => service.category === 'Treatments'
  );

  return (
    <Box
      borderRadius="0.5rem"
      border="1px solid"
      minW={'20.625rem'}
      borderColor="border.subtle"
      padding="0.75rem 1rem"
      bg="white"
    >
      {isRefreshing && (
        <Flex
          justify="center"
          align="center"
          p="0.5rem"
          bg="blue.50"
          borderRadius="0.375rem"
          border="1px solid"
          borderColor="blue.200"
          mb="0.5rem"
        >
          <Spinner size="sm" color="blue.500" mr="0.5rem" />
          <Text fontSize="0.75rem" color="blue.700">
            Refreshing provider data...
          </Text>
        </Flex>
      )}
      <Stack gap="0.75rem">
        <Stack gap="0.5rem">
          <Flex alignItems="center" gap="0.75rem" flexWrap="wrap">
            <ProviderName
              provider={provider}
              duplicateProviders={duplicateProviders}
              mode="link"
              size="lg"
              truncate={false}
            />
            {isExternalContext && !isExternalProvider(provider) && (
              <ExternalAffordance entityType="provider" size="sm" />
            )}
            {(() => {
              const alreadyShownViaName = (provider.relatedPractices?.length ?? 0) > 1;
              if (alreadyShownViaName) return null;
              const unique = Array.from(
                new Map((practices || []).map((p) => [p.id, p])).values()
              );
              return unique.length > 1 ? <MultiClinicBadge practices={unique} /> : null;
            })()}
            {providerExternalTablespace && (
              <CustomFieldLabel
                customField="Tablespace"
                fieldValue={providerExternalTablespace}
                fieldValueBgColor="background.gray100"
              />
            )}
            {!providerIsExternal && (
              <ProviderPacStatus
                providerBridge={provider.providerBridge}
                relatedPractices={provider.relatedPractices}
              />
            )}
          </Flex>

          {/* Athena ID */}
          <CustomFieldLabel
            customField="Athena ID"
            fieldValue={
              getCustomFieldValue(
                provider?.customFields,
                'Athena-ID'
              ) || 'Not specified'
            }
            fieldValueBgColor={
              getCustomFieldValue(
                provider?.customFields,
                'Gender'
              ) === 'Male'
                ? 'blue.100'
                : getCustomFieldValue(
                    provider?.customFields,
                    'Gender'
                  ) === 'Female'
                ? 'background.pink100'
                : 'gray.100'
            }
            fieldValueTextColor={
              getCustomFieldValue(
                provider?.customFields,
                'Gender'
              ) === 'Male'
                ? 'blue.800'
                : getCustomFieldValue(
                    provider?.customFields,
                    'Gender'
                  ) === 'Female'
                ? 'text.pink800'
                : 'gray.800'
            }
          />

          {/* Specialty */}
          {provider.specialty && (
            <CustomFieldLabel
              customField="Specialty"
              fieldValue={provider.specialty}
              fieldValueBgColor="background.blue100"
              fieldValueTextColor="text.primary"
            />
          )}

          {/* Pronunciation */}
          {provider?.pronunciation && (
            <CustomFieldLabel
              customField="Pronunciation"
              fieldValue={`${provider?.pronunciation}`}
              fieldValueBgColor="background.teal100"
              fieldValueTextColor="text.primary"
            />
          )}

          {/* Nickname */}
          {provider?.nickname && (
            <CustomFieldLabel
              customField="Nickname"
              fieldValue={`${provider?.nickname}`}
              fieldValueBgColor="background.teal100"
              fieldValueTextColor="text.primary"
            />
          )}

          {/* Athena Department Link - component includes its own tooltip */}
          {(athenaData.departmentName ||
            athenaData.departmentLink ||
            athenaData.practiceName) && (
            <Box>
              <AthenaDepartmentLink
                departmentName={athenaData.departmentName}
                departmentLink={athenaData.departmentLink}
                practiceName={athenaData.practiceName}
                practiceLocation={athenaData.practiceLocation}
                practiceId={
                  provider.relatedPractices?.find(
                    (p) => p.name === athenaData.practiceName
                  )?.id
                }
                practiceAddress={constructPracticeAddress(
                  provider.relatedPractices?.find(
                    (p) => p.name === athenaData.practiceName
                  )
                )}
              />
            </Box>
          )}
          {provider?.npi && (
            <CustomFieldLabel
              customField="NPI"
              fieldValue={`${provider?.npi}`}
              fieldValueBgColor="background.teal100"
              fieldValueTextColor="text.primary"
            />
          )}
          {/* New Patient Status */}
          {newPatientProps && (
            <CustomFieldLabel
              customField="New Patient"
              fieldValue={newPatientProps.fieldValue}
              fieldValueBgColor={newPatientProps.fieldValueBgColor}
              fieldValueTextColor={
                newPatientProps.fieldValueTextColor
              }
              hideCustomFieldLabel={true}
            />
          )}
        </Stack>

        {/* Daily Alerts Card */}
        <DailyAlertsCard
          provider={provider}
          customFields={passedCustomFields}
          practices={practices}
          isCustomFieldsLoading={isCustomFieldsLoading}
          customFieldsError={customFieldsError}
          specialtyOptions={specialtyOptions}
          isSpecialtyOptionsLoading={isSpecialtyOptionsLoading}
        />

        {/* Office Notes Card */}
        <OfficeNotesCard
          provider={provider}
          customFields={passedCustomFields}
          practices={practices}
          isPracticesLoading={isPracticesLoading}
          isCustomFieldsLoading={isCustomFieldsLoading}
          customFieldsError={customFieldsError}
          specialtyOptions={specialtyOptions}
          isSpecialtyOptionsLoading={isSpecialtyOptionsLoading}
        />

        {/* Provider Absences Card */}
        <ProviderAbsencesCard
          key={`absences-${provider.id}`}
          providerId={provider.id}
          practices={practices || []}
          absences={absences || []}
          loading={loading}
          error={error}
          refreshAbsences={refreshAbsences}
        />

        {/* Expand/Collapse Button */}
        <Flex
          align="center"
          gap="0.5rem"
          cursor="pointer"
          width="fit-content"
          onClick={() => onToggle()}
        >
          {isOpen ? (
            <ChevronUpIcon
              color="background.brandLight"
              width="1.125rem"
              height="1.125rem"
            />
          ) : (
            <ChevronDownIcon
              color="background.brandLight"
              width="1.125rem"
              height="1.125rem"
            />
          )}
          <Text
            fontSize="0.875rem"
            fontWeight="500"
            lineHeight="1.25rem"
            color="background.brandLight"
          >
            {isOpen ? 'Collapse' : 'Expand'}
          </Text>
        </Flex>
        <Collapse in={isOpen} animateOpacity>
          <Stack gap="1rem">
            {/* Scheduling Section - Full Width */}
            {schedulingFields.length > 0 && (
              <Box width="100%">
                <Text
                  mt="0.75rem"
                  fontSize={'0.875rem'}
                  fontWeight={700}
                  lineHeight={'120%'}
                  color="text.secondary"
                  mb="0.5rem"
                >
                  Scheduling
                </Text>
                <Box
                  border="1px solid"
                  borderColor={'border.subtle'}
                  borderRadius={'0.25rem'}
                >
                  {schedulingFields.map((service, index) => (
                    <Flex
                      key={service.name}
                      direction={
                        service.name === 'Additional-Location-Days'
                          ? 'column'
                          : 'row'
                      }
                      align={
                        service.name === 'Additional-Location-Days'
                          ? 'stretch'
                          : 'flex-start'
                      }
                      justify={
                        service.name === 'Additional-Location-Days'
                          ? 'flex-start'
                          : 'space-between'
                      }
                      p="0.5rem 1rem"
                      bg={
                        index % 2 === 0
                          ? 'white'
                          : 'background.secondary'
                      }
                      gap={
                        service.name === 'Additional-Location-Days'
                          ? '0.25rem'
                          : '1.2rem'
                      }
                      minH="auto"
                    >
                      <Text
                        fontSize="0.875rem"
                        fontWeight="400"
                        lineHeight={'1.25rem'}
                        color="text.primary"
                        flex="1"
                        wordBreak="break-word"
                      >
                        {getServiceLabel(service.name)}
                      </Text>
                      <Box flexShrink={0}>
                        {service.name === 'Medicare-Wellness' &&
                        provider.customFields['Medicare-Wellness'] ? (
                          <Box
                            p="0.5rem"
                            borderRadius={'0.25rem'}
                            background={'background.purple100'}
                            width="fit-content"
                            maxW="100%"
                          >
                            {provider.customFields[
                              'Medicare-Wellness'
                            ] && (
                              <ExtractMedicareWellness
                                customField={
                                  provider?.customFields[
                                    'Medicare-Wellness'
                                  ]
                                }
                              />
                            )}
                          </Box>
                        ) : service.name ===
                          'Supervision-Physician' ? (
                          <Flex direction="column" gap="0.25rem">
                            {(() => {
                              const customField =
                                provider?.customFields[
                                  'Supervision-Physician'
                                ];
                              if (
                                customField &&
                                Array.isArray(customField) &&
                                customField[0]?.metadata?.providers
                              ) {
                                const providers = customField[0]
                                  .metadata.providers as {
                                  providerId: string;
                                  providerLabel: string;
                                }[];
                                if (providers.length > 0) {
                                  return (
                                    <>
                                      <Text
                                        fontSize="0.875rem"
                                        fontWeight="700"
                                        color="text.primary"
                                        lineHeight="1.25rem"
                                      >
                                        {service.detail || supervisionRoleLabel}
                                      </Text>
                                      <HStack
                                        spacing={1}
                                        flexWrap="wrap"
                                      >
                                        {providers.map(
                                          (supervisingProvider) => (
                                            <Tag
                                              key={
                                                supervisingProvider.providerId
                                              }
                                              size="sm"
                                              colorScheme="blue"
                                              borderRadius="full"
                                              variant="outline"
                                              cursor="pointer"
                                              onClick={() =>
                                                navigate(
                                                  `/providers/${supervisingProvider.providerId}`
                                                )
                                              }
                                              _hover={{
                                                bg: 'blue.50',
                                                borderColor:
                                                  'blue.400',
                                              }}
                                              transition="all 0.15s ease-in-out"
                                              fontSize="0.75rem"
                                              fontWeight="500"
                                            >
                                              <TagLabel>
                                                {
                                                  supervisingProvider.providerLabel
                                                }
                                              </TagLabel>
                                            </Tag>
                                          )
                                        )}
                                      </HStack>
                                    </>
                                  );
                                }
                              }
                              if (
                                service.isRichText &&
                                service.detail
                              ) {
                                return (
                                  <Box
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight="1.25rem"
                                    wordBreak="break-word"
                                    overflowWrap="anywhere"
                                    textAlign="right"
                                    minWidth={0}
                                    maxW="200px"
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeHtml(
                                        service.detail
                                      ),
                                    }}
                                  />
                                );
                              }
                              return (
                                <Text
                                  fontSize="0.875rem"
                                  fontWeight="700"
                                  color="text.primary"
                                  lineHeight="1.25rem"
                                  wordBreak="break-word"
                                  textAlign="right"
                                  maxW="200px"
                                >
                                  {getFieldValue(service) || '-'}
                                </Text>
                              );
                            })()}
                          </Flex>
                        ) : service.name === 'Cross-Coverage' ? (
                          <CrossCoverageAllApptsBadge
                            customField={
                              provider?.customFields[
                                'Cross-Coverage'
                              ] as CustomFieldValue[] | undefined
                            }
                          />
                        ) : service.name === 'Cross-Coverage-Sick' ? (
                          <CrossCoverageSickBadge
                            scope="clinic"
                            customField={
                              provider?.customFields[
                                'Cross-Coverage-Sick'
                              ] as CustomFieldValue[] | undefined
                            }
                          />
                        ) : service.name ===
                          'Cross-Coverage-for-Sick-Market' ? (
                          <CrossCoverageSickBadge
                            scope="market"
                            customField={
                              provider?.customFields[
                                'Cross-Coverage-for-Sick-Market'
                              ] as CustomFieldValue[] | undefined
                            }
                          />
                        ) : service.name === 'Closest-Urgent-Care' ? (
                          <ClosestUrgentCareBadge
                            customField={
                              provider?.customFields[
                                'Closest-Urgent-Care'
                              ] as CustomFieldValue[] | undefined
                            }
                          />
                        ) : service.name === 'New-Telehealth' ||
                          service.name ===
                            'Out-Of-State-Telehealth' ||
                          service.name === 'Takes-Walk-Ins' ? (
                          (() => {
                            const value = getFieldValue(service);
                            const yesNoRender =
                              renderYesNoValue(value);
                            return (
                              yesNoRender || (
                                <Text
                                  fontSize="0.875rem"
                                  fontWeight="700"
                                  color="text.primary"
                                  lineHeight="1.25rem"
                                >
                                  -
                                </Text>
                              )
                            );
                          })()
                        ) : service.name === 'Telehealth' ? (
                          (() => {
                            const field =
                              provider?.customFields?.['Telehealth'];
                            if (
                              field &&
                              Array.isArray(field) &&
                              field.length > 0
                            ) {
                              const value = getFieldValue(service);
                              const details = String(
                                field[0]?.metadata
                                  ?.telehealthDetails || ''
                              );
                              const normalized = value
                                .toLowerCase()
                                .trim();
                              const isYes =
                                normalized === 'yes' ||
                                normalized.startsWith('yes');
                              const isNo = normalized === 'no';

                              if (isYes || isNo) {
                                return (
                                  <Flex
                                    direction="column"
                                    gap="0.25rem"
                                  >
                                    {renderYesNoValue(value)}
                                    {isYes && details && (
                                      <Text
                                        fontSize="0.75rem"
                                        color="gray.600"
                                        fontStyle="italic"
                                        textAlign="center"
                                      >
                                        {details}
                                      </Text>
                                    )}
                                  </Flex>
                                );
                              }
                            }
                            return (
                              <Text
                                fontSize="0.875rem"
                                fontWeight="700"
                                color="text.primary"
                                lineHeight="1.25rem"
                              >
                                -
                              </Text>
                            );
                          })()
                        ) : (() => {
                            // For all other fields, use getFieldValue to ensure we get the correct value
                            // This handles cases where buildServices returns empty detail for special fields
                            const value = getFieldValue(service);
                            const yesNoRender =
                              renderYesNoValue(value);
                            if (yesNoRender) {
                              return yesNoRender;
                            }
                            // If not yes/no, fall through to existing rendering logic
                            return null;
                          })() ||
                          (service.isRichText && service.detail) ? (
                          <Text
                            fontSize="0.875rem"
                            fontWeight="700"
                            color="text.primary"
                            lineHeight="1.25rem"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(service.detail),
                            }}
                            wordBreak="break-word"
                            textAlign={
                              service.name === 'Additional-Location-Days'
                                ? 'left'
                                : 'right'
                            }
                            maxW={
                              service.name === 'Additional-Location-Days'
                                ? undefined
                                : '200px'
                            }
                          />
                        ) : (
                          <Text
                            fontSize="0.875rem"
                            fontWeight="700"
                            color="text.primary"
                            lineHeight="1.25rem"
                            wordBreak="break-word"
                            textAlign={
                              service.name === 'Additional-Location-Days'
                                ? 'left'
                                : 'right'
                            }
                            maxW={
                              service.name === 'Additional-Location-Days'
                                ? undefined
                                : '200px'
                            }
                          >
                            {service.detail || '-'}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  ))}
                </Box>
              </Box>
            )}

            {/* Treatments Section - Full Width */}
            {treatmentFields.length > 0 && (
              <Box width="100%">
                <Text
                  mt="0.75rem"
                  fontSize={'0.875rem'}
                  fontWeight={700}
                  lineHeight={'120%'}
                  color="text.secondary"
                  mb="0.5rem"
                >
                  Treatments
                </Text>
                <Box
                  border="1px solid"
                  borderColor={'border.subtle'}
                  borderRadius={'0.25rem'}
                >
                  {treatmentFields.map((service, index) => (
                    <Flex
                      key={service.name}
                      align="flex-start"
                      justify="space-between"
                      p="0.5rem 1rem"
                      bg={
                        index % 2 === 0
                          ? 'white'
                          : 'background.secondary'
                      }
                      gap="1.2rem"
                      minH="auto"
                    >
                      <Text
                        fontSize="0.875rem"
                        fontWeight="400"
                        lineHeight={'1.25rem'}
                        color="text.primary"
                        flex="1"
                        wordBreak="break-word"
                      >
                        {getServiceLabel(service.name)}
                      </Text>
                      <Box flexShrink={0}>
                        {(() => {
                          // Use getFieldValue to ensure we get the correct value for all fields
                          const value = getFieldValue(service);
                          const yesNoRender = renderYesNoValue(value);
                          if (yesNoRender) {
                            return yesNoRender;
                          }
                          // If not yes/no, fall through to existing rendering logic
                          return null;
                        })() ||
                        (service.isRichText && service.detail) ? (
                          <Text
                            fontSize="0.875rem"
                            fontWeight="700"
                            color="text.primary"
                            lineHeight="1.25rem"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(service.detail),
                            }}
                            wordBreak="break-word"
                            textAlign="right"
                            maxW="200px"
                          />
                        ) : (
                          <Text
                            fontSize="0.875rem"
                            fontWeight="700"
                            color="text.primary"
                            lineHeight="1.25rem"
                            wordBreak="break-word"
                            textAlign="right"
                            maxW="200px"
                          >
                            {getFieldValue(service) || '-'}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  ))}
                </Box>
              </Box>
            )}

            {/* Show message if no fields are available */}
            {schedulingFields.length === 0 &&
              treatmentFields.length === 0 && (
                <Box
                  textAlign="center"
                  py="1.5rem"
                  px="1rem"
                  bg="gray.50"
                  borderRadius="0.375rem"
                  border="1px dashed"
                  borderColor="border.subtle"
                >
                  <Text
                    fontSize="0.875rem"
                    color="text.secondary"
                    fontWeight="500"
                  >
                    No services available for this provider type.
                  </Text>
                </Box>
              )}
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};

export default ProviderCompactCard;
